import { XOR } from "ts-essentials";
import {
    Gender,
    MaritalStatus,
    PersonInput,
    WorkerProfileInput,
} from "../../__generated__/graphql";
import { hasPartner, isAdult } from "../../hooks/use-get-is-worker-profile-complete";

type PartnerFormData = Pick<
    PersonInput,
    "firstName" | "lastName" | "avsNumber" | "nationality" | "permitType"
>;

export type CivilStateFormData = {
    avsNumber: string;
    hasNoAvsNumber: boolean;
} & XOR<
    { hasOtherActivity?: false },
    {
        hasOtherActivity: true;
        otherActivityPercent: number;
    }
> & {
        maritalStatus: MaritalStatus;
        churchMember?: boolean;
        sleepsInSwitzerland?: boolean;
        weeklyResidenceCanton?: string;
        partner?: PartnerFormData &
            XOR<
                { hasRevenue?: false },
                {
                    hasRevenue: true;
                    monthlySalary: number;
                }
            >;
    };

export const toCivilStateChanges = (
    data: CivilStateFormData,
    { paysWithholdingTax, livesAbroad }: { paysWithholdingTax: boolean; livesAbroad: boolean },
): WorkerProfileInput => ({
    // Send null to actively clear a stored AVS when the worker
    // declares having none; undefined leaves an existing value untouched.
    avsNumber: data.hasNoAvsNumber ? null : data.avsNumber || undefined,
    maritalStatus: data.maritalStatus,
    hasOtherActivity: data.hasOtherActivity,
    otherActivityPercent: data.otherActivityPercent,
    ...(paysWithholdingTax ? { churchMember: data.churchMember } : {}),
    // A worker living in Switzerland is never asked: their address gives their canton.
    ...(livesAbroad
        ? { weeklyResidenceCanton: data.sleepsInSwitzerland ? data.weeklyResidenceCanton : null }
        : {}),
    partner:
        paysWithholdingTax && hasPartner(data.maritalStatus)
            ? {
                  firstName: data.partner?.firstName || undefined,
                  lastName: data.partner?.lastName || undefined,
                  avsNumber: data.partner?.avsNumber || null,
                  gender: Gender.Unknown,
                  monthlySalary: data.partner?.hasRevenue
                      ? data.partner.monthlySalary
                          ? data.partner.monthlySalary * 100
                          : null
                      : 0, // set it to null to "remove" the value, undefined will remove the property
                  // Only asked to workers living in Switzerland; a Swiss partner holds no permit.
                  ...(!livesAbroad
                      ? {
                            nationality: data.partner?.nationality,
                            permitType:
                                data.partner?.nationality === "CH"
                                    ? null
                                    : data.partner?.permitType,
                        }
                      : {}),
              }
            : undefined,
});

export type ChildFormData = Pick<
    PersonInput,
    "firstName" | "lastName" | "birthdate" | "gender" | "avsNumber" | "inFirstEducation"
>;

/**
 * The first-education question is only asked from 18: a minor is sent with null, clearing an
 * answer given before their birthdate was corrected.
 */
export const toChildrenInput = (children: ChildFormData[]): PersonInput[] =>
    children.map((child) => ({
        ...child,
        inFirstEducation: isAdult(child.birthdate) ? child.inFirstEducation : null,
    }));
